import React, { useRef } from 'react';
import AuthWrapper1 from './AuthWrapper1';
import googleIcon from '../../assets/google_icon.png';
import linkedInIcon from '../../assets/linkedin_icon.png';
import appleIcon from '../../assets/apple_icon.png';
import { Box, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import CustomBackgroundComponent from '../../ui-components/CustomBackgroundComponent';
import BackrrLogoImage from '../../ui-components/BackrrLogoImage';
import { useTheme } from '@emotion/react';
import CustomInputField from '../../ui-components/CustomInputField';
import { WHITEBG } from '../../ui-components/CustomButton';
import { Primary, Secondary } from 'ui-components/Buttons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginViaEmail } from '../../api';
import toast from 'react-hot-toast';
import CustomToast from '../../ui-components/CustomToast';
import Cookies from "js-cookie";
import { auth, provider, signInWithPopup, signInWithRedirect, signOut } from "../../firebase";
import * as jwt_decode from 'jwt-decode';
import LinkedinLogin from './LinkedinLogin';
import { useConfig } from '../../dynamic-configuration/configContext';
import { checkForDomain, navigateToMain, redirectTo, setTokenForAuthorization, setUserType } from 'utils/index';
import { useToast } from 'context/ToastContext';
import GoogleLogin from './GoogleLogin';
import { RESET_STATE } from 'store/actions';
import { useDispatch } from 'react-redux';

export const INPUT_SX = {
  m: 0,
  "& .MuiFilledInput-root": {
    borderRadius: "10px",
    border: "1px solid #E4E7EC",
    backgroundColor: "#FFFFFF",
  },
  "& .MuiFilledInput-root.Mui-focused": {
    borderColor: "#101828",
  },
  "& .MuiFilledInput-input": {
    padding: "15px 16px",
  },
};

const AuthLogin = () => {
  const { config } = useConfig()
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [userData, setUserData] = React.useState(null);
  const [loginMethod, setLoginMethod] = React.useState("email");
  const { mutateAsync: loginViaEmailAsync, status: LoginViaEmailStatus, error: LoginViaEmailError, isLoading, data: loginViaEmailResponse } = LoginViaEmail();
  const [emailValue, setEmailValue] = React.useState("");
  const [emailError, setEmailError] = React.useState(false);
  const [isLogoutComplete, setIsLogoutComplete] = React.useState(false);

  const handleLogin = React.useCallback(() => {
    const currentIP = window.location.host;
    setLoginMethod("email");
    loginViaEmailAsync({
      "email": emailValue,
      "devices": {
        "device_token": `${Math.random() * 10}`,
        "device_type": "web"
      },
      "last_login_ip": `${currentIP}`,
      "auth_provider": "email",
      ...(searchParams.get("utm_source") ? { utm_source: searchParams.get("utm_source") } : {}),
      ...(searchParams.get("utm_campaign") ? { utm_campaign: searchParams.get("utm_campaign") } : {})
    }, {
      onSuccess: (verifyResponse) => {
        // Remember where the user was headed so OTP verification can return them there -
        // but only for a real in-app destination. This used to test window.location.SEARCH
        // (the query string) against "/login", which can never match, so the current path
        // was always stored. AuthenticationRoutes renders the login screen from a "*" route
        // without changing the URL, so getting logged out on /profile left pathname=/profile
        // and the next login landed there instead of Deal Flow.
        const currentPath = window.location?.pathname || "";
        const isAuthPath =
          currentPath === "/" ||
          currentPath.startsWith("/login") ||
          currentPath.startsWith("/verification") ||
          currentPath.startsWith("/callback");
        if (!isAuthPath) {
          Cookies.set("pathname", currentPath + (window.location?.search || ""));
        } else {
          Cookies.remove("pathname");
        }
        const response = verifyResponse?.data?.data;
        if (verifyResponse?.data?.statusCode !== 403) {
          if (searchParams.get("utm_campaign")) {
            Cookies.set("utm_campaign", searchParams.get("utm_campaign"));
          }
          Cookies.set("email", emailValue);
          Cookies.set("step", Number(response?.userData?.step))
          Cookies.get("email") && navigate("/verification");
        }
        else {
          setEmailError(true);
        }
      },
      onError: () => {
        setEmailError(true);
      }
    });
  }, [emailValue, loginViaEmailAsync, navigate]);

  const handleSubmit = React.useCallback(() => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emailPattern?.test(emailValue)) {
      handleLogin();
    }
    else {
      setEmailError(true);
    }
  }, [emailValue, handleLogin]);

  const handleEmailChange = React.useCallback((event) => {
    let inputValue = event?.target?.value.trim();
    if (inputValue.length <= 60) {
      if (inputValue.startsWith("mailto:")) {
        inputValue = inputValue?.replace(/^mailto:/, "")?.replaceAll(" ", "");
      }
      setEmailValue(inputValue?.toLowerCase());
    }
    setEmailError(false);
  }, []);

  const handleKeyDown = React.useCallback((e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleLogoClick = React.useCallback((e) => {
    const currentIP = window.location.host;
    navigateToMain(currentIP);
  }, []);

  const handleRedirectClick = React.useCallback(() => {
    const currentIP = window.location.host;
    redirectTo(currentIP);
  }, []);

  // Logout effect - runs only once on component mount
  React.useEffect(() => {
    let isMounted = true;

    const handleLogOut = () => {
      if (!isMounted) return;

      try {
        dispatch({ type: RESET_STATE });

        // Clear specific cookies
        const cookiesToRemove = [
          'X-INV-JWT',
          'X-INV-ID',
          'is_profile_complete',
          'email',
          'userDetails',
          'step'
        ];

        cookiesToRemove.forEach(cookieName => {
          Cookies.remove(cookieName, { path: '/' });
          Cookies.remove(cookieName, { domain: checkForDomain() });
        });

        // Handle LinkedIn logout
        const providerCookies = Cookies.get("provider");
        // if (providerCookies === "linkedin") {
        //   const url = 'https://www.linkedin.com/m/logout';
        //   const win = window.open(url, '_blank', 'toolbar=no,status=no,menubar=no,scrollbars=no,resizable=no,left=10000, top=10000, width=1, height=1, visible=none');
        //   setTimeout(() => {
        //     if (win && !win.closed) {
        //       win.close();
        //     }
        //   }, 3000);
        // }

        // Clear all cookies
        const cookies = document.cookie.split(";");
        cookies.forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          if (name) {
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
          }
        });

        if (isMounted) {
          setIsLogoutComplete(true);
        }
      } catch (error) {
        console.error('Error during logout:', error);
        if (isMounted) {
          setIsLogoutComplete(true);
        }
      }
    };

    handleLogOut();

    return () => {
      isMounted = false;
    };
  }, [dispatch]); // Only depend on dispatch

  // Don't render the form until logout is complete
  if (!isLogoutComplete) {
    return (
      <Grid item xs={12} display={"flex"} justifyContent={"center"} alignItems={"center"} minHeight={"400px"}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography sx={{ fontSize: { xs: "1.75rem", sm: "2rem" }, fontWeight: 700, color: "#101828", textAlign: "center", lineHeight: 1.2 }}>
          For Investors
        </Typography>
        <Typography sx={{ color: "#667085", textAlign: "center", mt: 1, fontSize: "1rem" }}>
          Continue with Backrr Account
        </Typography>

        <Box sx={{ mt: { xs: 3, sm: 4 } }}>
          <CustomInputField
            value={emailValue}
            onKeyDown={handleKeyDown}
            onChange={handleEmailChange}
            hasError={emailError}
            errorMessage={emailValue?.length > 0 ? emailError ? "Something went wrong." : "Please enter a valid email." : "Email is required."}
            label="Email"
            labelStyle={{ textTransform: "none", color: "#344054", fontWeight: 500, fontSize: "0.875rem", marginBottom: "8px" }}
            fullWidth
            type="email"
            placeholder="Enter your email"
            sx={INPUT_SX}
          />
        </Box>

        <Primary
          disabled={isLoading && loginMethod === "email"}
          sx={{ mt: 3, fontSize: '1rem' }}
          fullWidth
          onClick={handleSubmit}
        >
          {isLoading && loginMethod === "email" ?
            <CircularProgress sx={{ color: "white", marginInline: 'auto' }} size={'1.4rem'} />
            : "Continue with Email"}
        </Primary>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 3 }}>
          <Divider sx={{ flex: 1, borderColor: "#E5E7EB" }} />
          <Typography sx={{ color: "#98A2B3", fontSize: "0.95rem" }}>or</Typography>
          <Divider sx={{ flex: 1, borderColor: "#E5E7EB" }} />
        </Box>

        <Box sx={{ display: "grid", gap: "1rem" }}>
          {config?.settings.googleLogin && <GoogleLogin />}
          {config?.settings.linkedInLogin && <LinkedinLogin />}
        </Box>

        <Typography sx={{ textAlign: "center", color: "#667085", mt: 3, fontSize: "0.95rem" }}>
          Are you a Startup?{" "}
          <Box
            component="span"
            tabIndex={0}
            role="button"
            onClick={handleRedirectClick}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { handleRedirectClick(); } }}
            sx={{ color: "#5046E5", fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          >
            Click here
          </Box>
        </Typography>
      </Box>
    </>
  )
};

export default AuthLogin